<DOMContainer width="100%" height="100%">
    <div class="rpg-ui-title-screen rpg-anim-fade-in" style={{backgroundImage: backgroundImageStyle()}}>
        <div class="rpg-ui-title-screen-header rpg-anim-float">
            <div class="rpg-ui-title-screen-title">{titleText()}</div>
            @if (subtitleText()) {
                <div class="rpg-ui-title-screen-subtitle">{subtitleText()}</div>
            }
        </div>
        <Navigation tabindex={selectedEntry} controls={controls}>
            <div class="rpg-ui-menu rpg-ui-title-screen-menu rpg-anim-slide-up">
                @for ((entry,index) of entryList()) {
                    <div
                        class="rpg-ui-menu-item"
                        class={{disabled: isEntryDisabled(entry)}}
                        data-selected={selectedEntry() === index ? "true" : "false"}
                        tabindex={index}
                        click={selectEntry(index)}
                    >{entry.label}</div>
                }
            </div>
        </Navigation>
        @if (versionText()) {
            <div class="rpg-ui-title-screen-version">{versionText()}</div>
        }
    </div>
</DOMContainer>

<script>
    import { signal, computed, effect, mount } from "canvasengine";
    import { PrebuiltGui } from "@rpgjs/common";
    import { inject } from "../../core/inject";
    import { RpgClientEngine } from "../../RpgClientEngine";
    import { RpgGui } from "../../Gui/Gui";
    import { getKeyboardControlBind } from "../../services/actionInput";
    import { menuGamepadControls } from "../../services/gamepadControls";

    const engine = inject(RpgClientEngine);
    const { t } = engine.i18n();
    const guiService = inject(RpgGui);
    const keyboardControls = engine.globalConfig.keyboardControls;

    const { data, onInteraction } = defineProps({
        entries: {
            default: () => []
        },
        saveLoad: {
            default: () => ({})
        },
        localActions: {
            default: false
        },
        backgroundMusic: {
            default: null
        },
        backgroundImage: {
            default: null
        }
    });

    const { entries, title, subtitle, version, saveLoad, localActions, backgroundMusic, backgroundImage } = data();

    const defaultEntries = [
        { id: "start", label: t("rpg.title.start") },
        { id: "load", label: t("rpg.title.load") }
    ];

    const resolveProp = (value) => typeof value === "function" ? value() : value;
    const titleText = computed(() => resolveProp(title) || t("rpg.title.default"));
    const subtitleText = computed(() => resolveProp(subtitle) || "");
    const versionText = computed(() => resolveProp(version) || "");
    const localActionsEnabled = computed(() => resolveProp(localActions) === true);
    const backgroundMusicId = computed(() =>
        resolveProp(backgroundMusic)
        || engine.globalConfig?.menus?.titleScreen?.settings?.backgroundMusic
        || ""
    );
    const backgroundImageSource = computed(() =>
        resolveProp(backgroundImage)
        || engine.globalConfig?.menus?.titleScreen?.settings?.backgroundImage
        || ""
    );
    const backgroundImageStyle = computed(() => {
        const source = backgroundImageSource();
        return source ? `url(${source})` : "none";
    });

    const entryList = computed(() => {
        const list = resolveProp(entries);
        if (Array.isArray(list) && list.length) return list;
        return defaultEntries;
    });

    const isEntryDisabled = (entry) => {
        if (!entry) return true;
        if (entry.disabled) return true;
        if (entry.enabled === false) return true;
        return false;
    };

    const selectedEntry = signal(0);
    const selectableIndexes = computed(() => {
        const list = entryList();
        return list
            .map((entry, index) => (isEntryDisabled(entry) ? null : index))
            .filter((value) => value !== null);
    });

    effect(() => {
        const available = selectableIndexes();
        if (!available.length) return;
        const current = selectedEntry();
        if (!available.includes(current)) {
            selectedEntry.set(available[0]);
        }
    });

    const moveSelection = (delta) => {
        const available = selectableIndexes();
        if (!available.length) return;
        const current = selectedEntry();
        const currentIndex = Math.max(0, available.indexOf(current));
        const nextIndex = (currentIndex + delta + available.length) % available.length;
        selectedEntry.set(available[nextIndex]);
        engine.playUiSound("navigate");
    };

    const triggerSelect = (index) => {
        const list = entryList();
        const entry = list[index];
        if (!entry || isEntryDisabled(entry)) {
            engine.playUiSound("error");
            return;
        }
        engine.playUiSound("confirm");
        if (localActionsEnabled()) {
            if (entry.id === "start") {
                guiService.hide(PrebuiltGui.TitleScreen);
            }
            if (entry.id === "load") {
                const config = resolveProp(saveLoad) || {};
                const slots = Array.isArray(config.slots) ? config.slots : [null, null, null];
                guiService.display(PrebuiltGui.Save, {
                    ...config,
                    mode: config.mode || "load",
                    slots
                });
            }
        }
        if (onInteraction) {
            onInteraction("select", { id: entry.id, index, entry });
        }
    };

    function selectEntry(index) {
        return function() {
            selectedEntry.set(index);
            engine.playUiSound("navigate");
            triggerSelect(index);
        };
    }

    const controls = signal({
        up: {
            repeat: true,
            bind: keyboardControls.up,
            throttle: 150,
            keyDown() {
                if (guiService.isDisplaying(PrebuiltGui.Save)) return;
                moveSelection(-1);
            }
        },
        down: {
            repeat: true,
            bind: keyboardControls.down,
            throttle: 150,
            keyDown() {
                if (guiService.isDisplaying(PrebuiltGui.Save)) return;
                moveSelection(1);
            }
        },
        action: {
            bind: getKeyboardControlBind(keyboardControls.action),
            keyDown() {
                if (guiService.isDisplaying(PrebuiltGui.Save)) return;
                triggerSelect(selectedEntry());
            }
        },
        gamepad: menuGamepadControls
    });

    mount(() => {
        engine.playUiSound("open");
        const music = backgroundMusicId();
        if (music) {
            engine.music.contextId = "title-screen";
            void engine.music.enter(music, {
                volume: 0.8,
                mapVolume: 0,
                fadeInMs: 600,
                fadeOutMs: 400
            });
        }
        return () => {
            if (engine.music.contextId !== "title-screen") return;
            engine.music.contextId = undefined;
            engine.music.leave({ exitDelayMs: 0, fadeOutMs: 400 });
        };
    });
</script>
